/**
 * Auth Faza 2 — Compact assign-to dropdown for the Lead/Enrollment drawers.
 *
 * - Owner + admin_fped see a select with all eligible operators
 *   (partner_operator + admin_fped, status ∈ active|invited).
 * - Other roles (partner_operator, viewer) see a read-only label.
 * - Optimistic UI: switches immediately, rolls back + toasts on error.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export default function AssignSelector({
  endpoint,            // "leads/{id}/assign" or "enrollments/{id}/assign"
  docId,
  currentAssignedToUserId,
  currentAssignedToEmail,
  canManage,           // true for owner + admin_fped
  onChanged,           // (newAssignment) => void
  testIdPrefix = "assign",
  showLabel = true,    // hide internal "Atribuit lui" label when caller renders its own
}) {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    setLoading(true);
    api.get("/admin/users").then((r) => {
      if (cancelled) return;
      const items = Array.isArray(r.data?.items) ? r.data.items : (r.data || []);
      const eligible = items.filter(
        (u) =>
          (u.role === "partner_operator" || u.role === "admin_fped") &&
          (u.status === "active" || u.status === "invited"),
      );
      setOperators(eligible);
    }).catch(() => {
      // Non-blocking — fall back to a read-only label.
      setOperators([]);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [canManage]);

  const currentLabel = useMemo(() => {
    if (currentAssignedToEmail) return currentAssignedToEmail;
    if (currentAssignedToUserId) return "(necunoscut)";
    return "Neatribuit";
  }, [currentAssignedToEmail, currentAssignedToUserId]);

  if (!canManage) {
    return (
      <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-readonly`}>
        {showLabel && (
          <span className="text-xs uppercase tracking-widest text-[#6B7280]">Atribuit lui</span>
        )}
        <span className="text-sm font-medium text-[#373A45]">{currentLabel}</span>
      </div>
    );
  }

  const handleChange = async (e) => {
    const value = e.target.value || null;
    setSaving(true);
    try {
      const r = await api.patch(`/admin/${endpoint}`, { assigned_to_user_id: value });
      onChanged?.(r.data);
      toast.success(value ? "Atribuit cu succes" : "Atribuire eliminată");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Atribuirea a eșuat.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1" data-testid={`${testIdPrefix}-editor`}>
      {showLabel && (
        <label className="text-xs uppercase tracking-widest text-[#6B7280]">Atribuit lui</label>
      )}
      <select
        className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-sm focus:border-[#965EA7] focus:outline-none"
        value={currentAssignedToUserId || ""}
        onChange={handleChange}
        disabled={saving || loading}
        data-testid={`${testIdPrefix}-select-${docId}`}
      >
        <option value="">Neatribuit</option>
        {operators.map((op) => (
          <option key={op.id} value={op.id}>
            {op.name || op.email} ({op.role === "partner_operator" ? "operator" : "admin"})
          </option>
        ))}
      </select>
    </div>
  );
}
